import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import { getIngredients } from "../../insumos/api/ingredientsApi";
import { BASE_UNIT_LABELS } from "../../insumos/utils/ingredientFormatters";
import SupplierFormModal from "../../proveedores/components/SupplierFormModal";
import { createSupplier, getSuppliers } from "../../proveedores/api/suppliersApi";
import { getPurchaseItems, savePurchaseItemsBatch } from "../api/purchaseItemsApi";
import { PURCHASE_UNIT_OPTIONS } from "../constants/purchaseUnits";
import {
  BASE_UNIT_OPTIONS,
  isWorkspaceRowDirty,
  makeBasePresentationWorkspaceRow,
  makeEmptyWorkspaceRow,
  normalizeDecimalInput,
  PURCHASE_ITEMS_BATCH_LIMIT,
  purchaseItemToWorkspaceRow,
  validateWorkspaceRow,
  workspaceRowToPayload,
} from "../utils/purchaseItemsWorkspaceModel";

const PAGE_LIMIT = 100;
const GRID_COLUMNS = "220px 160px 190px 155px 175px 130px 160px 48px";
const GRID_MIN_WIDTH = 1260;
const EDITABLE_FIELDS = [
  "supplier",
  "brand",
  "presentation",
  "purchaseUnit",
  "quantityPerUnit",
  "cost",
  "stockCountUnit",
];
const ERROR_FIELD_MAP = { supplierId: "supplier", ingredientId: "presentation" };

function errorToMessage(error, fallback = "No se pudo completar la operación.") {
  const message = error?.data?.detail || error?.data?.message || error?.response?.data?.detail || error?.response?.data?.message || error?.message;
  return Array.isArray(message) ? message.join(" | ") : message || fallback;
}

function listFrom(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  return Array.isArray(payload) ? payload : [];
}

async function loadEveryPage(loader, signal) {
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const payload = await loader(page, signal);
    if (signal.aborted) return [];
    all.push(...listFrom(payload));
    totalPages = Math.max(1, Number(payload?.meta?.totalPages) || 1);
    page += 1;
  } while (page <= totalPages);
  return all;
}

export function parseIngredientIds(search) {
  const params = new URLSearchParams(search);
  const raw = params.get("ids");
  if (!raw) return { ids: [], valid: false };
  const parts = raw.split(",").map((value) => value.trim());
  const ids = parts.map(Number);
  const valid = parts.length <= 200 && parts.every(Boolean) &&
    ids.every((id) => Number.isSafeInteger(id) && id > 0);
  return { ids: valid ? [...new Set(ids)] : [], valid };
}

function normalizeIngredient(item) {
  if (!item?.id) return null;
  return {
    ...item,
    id: Number(item.id),
    name: String(item.name || "Sin nombre"),
    category: item.category || item.ingredientCategory?.name || "Sin categoría",
  };
}

function normalizeSupplier(item) {
  if (!item?.id) return null;
  return { ...item, id: Number(item.id), name: String(item.name || "Sin nombre") };
}

function cloneRows(rows) {
  return rows.map((row) => ({ ...row, errors: { ...row.errors } }));
}

function rowIdentity(row) {
  return [
    Number(row.ingredient?.id),
    Number(row.supplier?.id),
    String(row.brand || "").trim().toLocaleLowerCase("es"),
    String(row.presentation || "").trim().toLocaleLowerCase("es"),
  ].join("\u0000");
}

function samePresentation(row, serverItem) {
  return rowIdentity(row) === rowIdentity(purchaseItemToWorkspaceRow(serverItem)) &&
    String(row.purchaseUnit || "") === String(serverItem.purchaseUnit || "") &&
    Number(normalizeDecimalInput(row.quantityPerUnit)) === Number(serverItem.quantityPerUnit) &&
    Number(normalizeDecimalInput(row.cost)) === Number(serverItem.cost) &&
    String(row.stockCountUnit || "") === String(serverItem.stockCountUnit || "");
}

function ConfirmChangesDialog({ open, discardOnly, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onClose={onCancel} PaperProps={{ sx: { borderRadius: "18px" } }}>
      <DialogTitle sx={{ fontWeight: 900, color: "primary.900" }}>Descartar cambios</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "neutral.700", fontWeight: 600 }}>
          {discardOnly ? "Se restaurarán los valores guardados y se quitarán las filas nuevas." : "Hay cambios sin guardar. Si continuás, se perderán."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button onClick={onConfirm} variant="contained">Descartar</Button>
      </DialogActions>
    </Dialog>
  );
}

function CellError({ message }) {
  return message ? <Typography title={message} sx={{ mt: 0.35, color: "error.main", fontSize: 10.5, fontWeight: 700 }}>{message}</Typography> : null;
}

function fieldSx(hasError) {
  return {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      minHeight: 38,
      borderRadius: "10px",
      bgcolor: hasError ? "rgba(211,47,47,.06)" : "white",
      fontSize: 12.5,
    },
    "& .MuiInputBase-input": { py: 0.85 },
  };
}

export default function PurchaseItemsWorkspacePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const cellRefs = useRef(new Map());
  const parsedScope = useMemo(() => parseIngredientIds(location.search), [location.search]);
  const [rows, setRows] = useState([]);
  const [baselineRows, setBaselineRows] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveSummary, setSaveSummary] = useState("");
  const [search, setSearch] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [supplierFormOpen, setSupplierFormOpen] = useState(false);

  const dirtyRows = useMemo(() => rows.filter(isWorkspaceRowDirty), [rows]);
  const hasPendingChanges = dirtyRows.length > 0;
  const navigationBlocker = useBlocker(({ currentLocation, nextLocation }) =>
    hasPendingChanges && currentLocation.pathname !== nextLocation.pathname
  );

  const loadWorkspace = useCallback(async (signal) => {
    if (!parsedScope.valid || !parsedScope.ids.length) {
      setIngredients([]);
      setRows([]);
      setBaselineRows([]);
      setLoadError("La URL debe incluir uno o más IDs de insumos válidos.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const [scopedIngredients, purchaseItems, activeSuppliers] = await Promise.all([
        loadEveryPage((page, requestSignal) => getIngredients({ page, limit: PAGE_LIMIT, ingredientIds: parsedScope.ids, isActive: true, signal: requestSignal }), signal),
        loadEveryPage((page, requestSignal) => getPurchaseItems({ page, limit: PAGE_LIMIT, ingredientIds: parsedScope.ids, isActive: true, signal: requestSignal }), signal),
        loadEveryPage((page, requestSignal) => getSuppliers({ page, limit: PAGE_LIMIT, isActive: true, signal: requestSignal }), signal),
      ]);
      if (signal.aborted) return;
      const normalizedIngredients = scopedIngredients.map(normalizeIngredient).filter(Boolean);
      const byId = new Map(normalizedIngredients.map((item) => [item.id, item]));
      const missingIds = parsedScope.ids.filter((id) => !byId.has(id));
      if (missingIds.length) {
        throw new Error("Uno o más insumos no existen, están inactivos o no pertenecen a la licencia actual.");
      }
      const orderedIngredients = parsedScope.ids.map((id) => byId.get(id));
      const nextRows = purchaseItems
        .filter((item) => item?.isActive !== false && byId.has(Number(item.ingredient?.id || item.ingredientId)))
        .map((item) => {
          const row = purchaseItemToWorkspaceRow(item);
          row.ingredient = byId.get(Number(row.ingredient?.id)) || row.ingredient;
          return row;
        });
      setIngredients(orderedIngredients);
      setRows(nextRows);
      setBaselineRows(cloneRows(nextRows));
      setSuppliers(activeSuppliers.map(normalizeSupplier).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" })));
    } catch (error) {
      if (!signal.aborted) setLoadError(errorToMessage(error, "No se pudieron cargar las presentaciones."));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [parsedScope.ids, parsedScope.valid]);

  useEffect(() => {
    const controller = new AbortController();
    loadWorkspace(controller.signal);
    return () => controller.abort();
  }, [loadWorkspace]);

  useEffect(() => {
    if (!hasPendingChanges) return undefined;
    const preventUnload = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [hasPendingChanges]);

  const groups = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("es");
    return ingredients.map((ingredient) => {
      const ingredientRows = rows.filter((row) => Number(row.ingredient?.id) === ingredient.id);
      const visibleRows = !term || ingredient.name.toLocaleLowerCase("es").includes(term)
        ? ingredientRows
        : ingredientRows.filter((row) => [row.supplier?.name, row.brand, row.presentation].some((value) => String(value || "").toLocaleLowerCase("es").includes(term)));
      return { ingredient, rows: visibleRows, totalRows: ingredientRows.length };
    }).filter((group) => !term || group.rows.length || group.ingredient.name.toLocaleLowerCase("es").includes(term));
  }, [ingredients, rows, search]);

  function updateRow(clientRowId, field, value) {
    setRows((current) => current.map((row) => row.clientRowId === clientRowId ? {
      ...row,
      [field]: value,
      rowState: row.id ? "edited" : "new",
      errors: { ...row.errors, [field]: "", presentation: field === "presentation" ? "" : row.errors.presentation },
    } : row));
    setSaveError("");
    setSaveSummary("");
  }

  function appendPresentation(ingredient, row) {
    const groupPending = dirtyRows.filter((row) => Number(row.ingredient?.id) === ingredient.id).length;
    if (groupPending >= PURCHASE_ITEMS_BATCH_LIMIT) {
      setSaveError(`El grupo ${ingredient.name} alcanzó el límite de ${PURCHASE_ITEMS_BATCH_LIMIT} filas pendientes.`);
      return;
    }
    setRows((current) => [...current, row]);
    setSearch("");
    requestAnimationFrame(() => cellRefs.current.get(`${row.clientRowId}:supplier`)?.focus());
  }

  function addPresentation(ingredient) {
    appendPresentation(ingredient, makeEmptyWorkspaceRow(ingredient));
  }

  function addBasePresentation(ingredient) {
    appendPresentation(ingredient, makeBasePresentationWorkspaceRow(ingredient));
  }

  function removeNewRow(clientRowId) {
    setRows((current) => current.filter((row) => row.clientRowId !== clientRowId));
  }

  function closeSupplierForm() {
    setSupplierFormOpen(false);
  }

  async function createSupplierForRow(payload) {
    const created = normalizeSupplier(await createSupplier(payload));
    if (!created) throw new Error("El proveedor se creó, pero no se recibió un ID válido.");

    setSuppliers((current) => [...current.filter((item) => item.id !== created.id), created]
      .sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" })));
    closeSupplierForm();
  }

  function setCellRef(rowId, field, node) {
    const key = `${rowId}:${field}`;
    if (node) cellRefs.current.set(key, node);
    else cellRefs.current.delete(key);
  }

  function navigateCell(event, rowId, field, groupRows) {
    if (!["Enter", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    const rowIndex = groupRows.findIndex((row) => row.clientRowId === rowId);
    const fieldIndex = EDITABLE_FIELDS.indexOf(field);
    let nextRow = rowIndex;
    let nextField = fieldIndex;
    if (event.key === "ArrowUp") nextRow -= 1;
    if (event.key === "ArrowDown") nextRow += 1;
    if (event.key === "Enter") {
      nextField += 1;
      if (nextField >= EDITABLE_FIELDS.length) { nextField = 0; nextRow += 1; }
    }
    const targetRow = groupRows[nextRow];
    const target = targetRow && cellRefs.current.get(`${targetRow.clientRowId}:${EDITABLE_FIELDS[nextField]}`);
    if (target) { event.preventDefault(); target.focus?.(); }
  }

  function discardChanges() {
    setRows(cloneRows(baselineRows));
    setSaveError("");
    setSaveSummary("");
    setConfirmDiscard(false);
  }

  async function reconcileAmbiguousGroup(ingredientId, pendingRows) {
    const controller = new AbortController();
    const serverItems = await loadEveryPage(
      (page, signal) => getPurchaseItems({ page, limit: PAGE_LIMIT, ingredientId, isActive: true, signal }),
      controller.signal
    );
    const replacements = new Map();
    const errors = new Map();
    pendingRows.forEach((row) => {
      const serverItem = row.id
        ? serverItems.find((item) => Number(item.id) === Number(row.id))
        : serverItems.find((item) => rowIdentity(purchaseItemToWorkspaceRow(item)) === rowIdentity(row));
      if (!serverItem) {
        errors.set(row.clientRowId, { presentation: "No se confirmó el guardado. La fila no existe en el servidor y puede reintentarse." });
      } else if (samePresentation(row, serverItem)) {
        replacements.set(row.clientRowId, purchaseItemToWorkspaceRow(serverItem));
      } else {
        errors.set(row.clientRowId, { presentation: "Existe una presentación con la misma identidad pero datos diferentes. Revisá el conflicto antes de reintentar." });
      }
    });
    return { replacements, errors };
  }

  async function saveChanges() {
    if (!dirtyRows.length || saving) return;
    setSaving(true);
    setSaveError("");
    setSaveSummary("");

    const snapshotRows = cloneRows(rows);
    const dirtyByIngredient = new Map();
    dirtyRows.forEach((row) => {
      const id = Number(row.ingredient?.id);
      dirtyByIngredient.set(id, [...(dirtyByIngredient.get(id) || []), row]);
    });
    const replacements = new Map();
    const errorsByRow = new Map();
    const successfulGroups = [];
    const failedGroups = [];

    // Each group remains atomic in the backend. Processing groups sequentially
    // avoids competing transactions recalculating the same dependent costs and
    // producing transient MySQL deadlocks.
    for (const [ingredientId, groupRows] of dirtyByIngredient) {
      const ingredient = ingredients.find((item) => item.id === ingredientId);
      if (groupRows.length > PURCHASE_ITEMS_BATCH_LIMIT) {
        groupRows.forEach((row) => errorsByRow.set(row.clientRowId, { presentation: `El grupo supera el límite de ${PURCHASE_ITEMS_BATCH_LIMIT} filas.` }));
        failedGroups.push(ingredient?.name || `Insumo #${ingredientId}`);
        continue;
      }
      let invalid = false;
      groupRows.forEach((row) => {
        const validation = validateWorkspaceRow(row);
        if (Object.keys(validation).length) {
          invalid = true;
          errorsByRow.set(row.clientRowId, validation);
        }
      });
      if (invalid) {
        failedGroups.push(ingredient?.name || `Insumo #${ingredientId}`);
        continue;
      }

      try {
        const response = await savePurchaseItemsBatch(groupRows.map(workspaceRowToPayload));
        const entries = Array.isArray(response?.data) ? response.data : [];
        entries.forEach((entry) => {
          if (entry?.clientRowId && entry?.item) replacements.set(entry.clientRowId, purchaseItemToWorkspaceRow(entry.item));
        });
        const confirmedClientIds = new Set(
          entries
            .filter((entry) => entry?.clientRowId && entry?.item)
            .map((entry) => entry.clientRowId),
        );
        if (groupRows.some((row) => !confirmedClientIds.has(row.clientRowId))) {
          throw new Error("La respuesta no confirmó todas las filas del grupo.");
        }
        successfulGroups.push(ingredient?.name || `Insumo #${ingredientId}`);
      } catch (error) {
        const backendErrors = Array.isArray(error?.data?.errors) ? error.data.errors : [];
        if (backendErrors.length) {
          backendErrors.forEach((entry) => {
            const clientRowId = entry.clientRowId || groupRows[entry.index]?.clientRowId;
            if (!clientRowId) return;
            const mappedField = ERROR_FIELD_MAP[entry.field] || entry.field;
            const field = EDITABLE_FIELDS.includes(mappedField) ? mappedField : "presentation";
            errorsByRow.set(clientRowId, { ...(errorsByRow.get(clientRowId) || {}), [field]: entry.message });
          });
          failedGroups.push(ingredient?.name || `Insumo #${ingredientId}`);
          continue;
        }
        const responseStatus = Number(error?.status || error?.response?.status);
        if (Number.isFinite(responseStatus) && responseStatus > 0) {
          const message = errorToMessage(error);
          groupRows.forEach((row) => errorsByRow.set(row.clientRowId, {
            presentation: message,
          }));
          failedGroups.push(ingredient?.name || `Insumo #${ingredientId}`);
          continue;
        }
        try {
          const reconciliation = await reconcileAmbiguousGroup(ingredientId, groupRows);
          reconciliation.replacements.forEach((value, key) => replacements.set(key, value));
          reconciliation.errors.forEach((value, key) => errorsByRow.set(key, value));
          if (reconciliation.errors.size) failedGroups.push(ingredient?.name || `Insumo #${ingredientId}`);
          else successfulGroups.push(ingredient?.name || `Insumo #${ingredientId}`);
        } catch (reconciliationError) {
          groupRows.forEach((row) => errorsByRow.set(row.clientRowId, { presentation: "No se pudo confirmar el estado del servidor. Recargá o reintentá más tarde." }));
          failedGroups.push(ingredient?.name || `Insumo #${ingredientId}`);
        }
      }
    }

    const nextRows = snapshotRows.map((row) => replacements.get(row.clientRowId) || (errorsByRow.has(row.clientRowId) ? { ...row, rowState: "error", errors: errorsByRow.get(row.clientRowId) } : row));
    const nextBaseline = cloneRows(baselineRows);
    replacements.forEach((savedRow) => {
      const index = nextBaseline.findIndex((row) => Number(row.id) === Number(savedRow.id));
      if (index >= 0) nextBaseline[index] = savedRow;
      else nextBaseline.push(savedRow);
    });
    setRows(nextRows);
    setBaselineRows(nextBaseline);
    setSaveSummary(`${successfulGroups.length} grupo(s) guardado(s) · ${failedGroups.length} grupo(s) pendiente(s).`);
    if (failedGroups.length) setSaveError(`Revisá los grupos pendientes: ${failedGroups.join(", ")}.`);
    setSaving(false);
  }

  const renderSupplierOptions = (current) => !current?.id || suppliers.some((item) => item.id === current.id) ? suppliers : [current, ...suppliers];

  return (
    <Stack spacing={2} sx={{ p: 2, flex: 1, minHeight: 0, overflow: "hidden" }}>
      <StockPageHeader title="Presentaciones de compra" onBack={() => navigate("/stock/base-de-datos/insumos")} backDisabled={saving} />
      <Box sx={{ p: 2, flex: 1, minHeight: 0, bgcolor: "neutral.300", borderRadius: "30px", overflow: "hidden" }}>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} justifyContent="space-between">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar insumo o presentación" size="small" InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ width: { xs: "100%", md: 320 }, "& .MuiOutlinedInput-root": { height: 38, borderRadius: "33px", bgcolor: "white" } }} />
              <Button
                variant="outlined"
                startIcon={<LocalShippingIcon />}
                disabled={saving}
                onClick={() => setSupplierFormOpen(true)}
                sx={{ borderRadius: "22px", textTransform: "none", fontWeight: 800, whiteSpace: "nowrap" }}
              >
                Agregar Proveedor
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button onClick={() => setConfirmDiscard(true)} disabled={!hasPendingChanges || saving}>Descartar cambios</Button>
              <Button variant="contained" onClick={saveChanges} disabled={!hasPendingChanges || saving}>
                {saving ? "Guardando..." : `Guardar cambios${dirtyRows.length ? ` (${dirtyRows.length})` : ""}`}
              </Button>
            </Stack>
          </Stack>
          {loadError ? <Alert severity="error">{loadError}</Alert> : null}
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          {saveSummary ? <Alert severity={saveError ? "warning" : "success"}>{saveSummary}</Alert> : null}

          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            {loading ? <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress size={30} /></Stack> : (
              <Stack spacing={1.5}>
                {groups.map(({ ingredient, rows: groupRows, totalRows }) => (
                  <Box key={ingredient.id} sx={{ bgcolor: "neutral.50", borderRadius: "18px", overflow: "hidden" }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ px: 2, py: 1.5, bgcolor: "primary.900", color: "white", justifyContent: "space-between", alignItems: { md: "center" } }}>
                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>{ingredient.name}</Typography>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 600, opacity: .88 }}>
                          {ingredient.category} · Unidad base: {BASE_UNIT_LABELS[ingredient.baseUnit] || ingredient.baseUnit}
                        </Typography>
                      </Box>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8}>
                        <Button disabled={saving} onClick={() => addBasePresentation(ingredient)} sx={{ border: "1px solid rgba(255,255,255,.72)", color: "white", borderRadius: "20px", textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: "rgba(255,255,255,.1)", borderColor: "white" } }}>
                          Usar insumo base como presentación
                        </Button>
                        <Button startIcon={<AddIcon />} disabled={saving} onClick={() => addPresentation(ingredient)} sx={{ bgcolor: "white", color: "primary.900", borderRadius: "20px", textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: "neutral.100" } }}>
                          Agregar presentación
                        </Button>
                      </Stack>
                    </Stack>
                    {totalRows === 0 ? (
                      <Typography sx={{ p: 2.5, color: "neutral.700", fontWeight: 700 }}>Sin presentaciones. Podés dejarlas pendientes o agregar una.</Typography>
                    ) : groupRows.length === 0 ? (
                      <Typography sx={{ p: 2.5, color: "neutral.700", fontWeight: 700 }}>Ninguna presentación coincide con la búsqueda.</Typography>
                    ) : (
                      <Box sx={{ overflowX: "auto" }}>
                        <Box role="grid" aria-label={`Presentaciones de ${ingredient.name}`} sx={{ minWidth: GRID_MIN_WIDTH }}>
                          <Box role="row" sx={{ display: "grid", gridTemplateColumns: GRID_COLUMNS, bgcolor: "neutral.600", color: "white" }}>
                            {["Proveedor", "Marca", "Presentación", "Unidad de compra", "Contenido por unidad", "Precio total", "Unidad de conteo", ""].map((label, index) => <Typography key={`${label}-${index}`} role="columnheader" sx={{ px: 1, py: 1.1, fontSize: 12, fontWeight: 800 }}>{label}</Typography>)}
                          </Box>
                          {groupRows.map((row) => {
                            const historicalUnit = row.id && row.purchaseUnit && !PURCHASE_UNIT_OPTIONS.includes(row.purchaseUnit);
                            return (
                              <Box key={row.clientRowId} role="row" sx={{ display: "grid", gridTemplateColumns: GRID_COLUMNS, alignItems: "start", borderBottom: "1px solid", borderColor: "neutral.300", bgcolor: row.rowState === "error" ? "rgba(211,47,47,.025)" : "white", "& > div": { px: .65, py: .8, minWidth: 0 } }}>
                                <Box role="gridcell">
                                  <Autocomplete size="small" disabled={saving} options={renderSupplierOptions(row.supplier)} value={row.supplier} onChange={(_, value) => updateRow(row.clientRowId, "supplier", value)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.id === value?.id} renderInput={(params) => <TextField {...params} inputRef={(node) => setCellRef(row.clientRowId, "supplier", node)} onKeyDown={(event) => navigateCell(event, row.clientRowId, "supplier", groupRows)} error={Boolean(row.errors.supplier)} placeholder="Proveedor" sx={fieldSx(row.errors.supplier)} />} />
                                  <CellError message={row.errors.supplier} />
                                </Box>
                                {[['brand', 'Marca', 150], ['presentation', 'Presentación', 200]].map(([field, placeholder, maxLength]) => (
                                  <Box role="gridcell" key={field}>
                                    <TextField disabled={saving} value={row[field]} onChange={(event) => updateRow(row.clientRowId, field, event.target.value)} onKeyDown={(event) => navigateCell(event, row.clientRowId, field, groupRows)} inputRef={(node) => setCellRef(row.clientRowId, field, node)} inputProps={{ maxLength }} error={Boolean(row.errors[field])} placeholder={placeholder} size="small" sx={fieldSx(row.errors[field])} />
                                    <CellError message={row.errors[field]} />
                                  </Box>
                                ))}
                                <Box role="gridcell">
                                  <Select disabled={saving} value={row.purchaseUnit} onChange={(event) => updateRow(row.clientRowId, "purchaseUnit", event.target.value)} onKeyDown={(event) => navigateCell(event, row.clientRowId, "purchaseUnit", groupRows)} ref={(node) => setCellRef(row.clientRowId, "purchaseUnit", node)} displayEmpty size="small" error={Boolean(row.errors.purchaseUnit)} sx={fieldSx(row.errors.purchaseUnit)}>
                                    <MenuItem value="" disabled>Seleccionar</MenuItem>
                                    {historicalUnit ? <MenuItem value={row.purchaseUnit}>{row.purchaseUnit} (histórico)</MenuItem> : null}
                                    {PURCHASE_UNIT_OPTIONS.map((unit) => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
                                  </Select>
                                  <CellError message={row.errors.purchaseUnit} />
                                </Box>
                                <Box role="gridcell">
                                  <TextField disabled={saving} value={row.quantityPerUnit} onChange={(event) => updateRow(row.clientRowId, "quantityPerUnit", event.target.value)} onKeyDown={(event) => navigateCell(event, row.clientRowId, "quantityPerUnit", groupRows)} inputRef={(node) => setCellRef(row.clientRowId, "quantityPerUnit", node)} inputProps={{ inputMode: "decimal", maxLength: 18 }} InputProps={{ endAdornment: <InputAdornment position="end">{ingredient.baseUnit}</InputAdornment> }} error={Boolean(row.errors.quantityPerUnit)} placeholder="0" size="small" sx={fieldSx(row.errors.quantityPerUnit)} />
                                  <CellError message={row.errors.quantityPerUnit} />
                                </Box>
                                <Box role="gridcell">
                                  <TextField disabled={saving} value={row.cost} onChange={(event) => updateRow(row.clientRowId, "cost", event.target.value)} onKeyDown={(event) => navigateCell(event, row.clientRowId, "cost", groupRows)} inputRef={(node) => setCellRef(row.clientRowId, "cost", node)} inputProps={{ inputMode: "decimal", maxLength: 18 }} error={Boolean(row.errors.cost)} placeholder="$ 0" size="small" sx={fieldSx(row.errors.cost)} />
                                  <CellError message={row.errors.cost} />
                                </Box>
                                <Box role="gridcell">
                                  <Select disabled={saving} value={row.stockCountUnit} onChange={(event) => updateRow(row.clientRowId, "stockCountUnit", event.target.value)} onKeyDown={(event) => navigateCell(event, row.clientRowId, "stockCountUnit", groupRows)} ref={(node) => setCellRef(row.clientRowId, "stockCountUnit", node)} size="small" error={Boolean(row.errors.stockCountUnit)} sx={fieldSx(row.errors.stockCountUnit)}>
                                    {BASE_UNIT_OPTIONS.map((unit) => <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>)}
                                  </Select>
                                  <CellError message={row.errors.stockCountUnit} />
                                </Box>
                                <Box role="gridcell">
                                  {!row.id ? <IconButton disabled={saving} aria-label="Quitar fila" onClick={() => removeNewRow(row.clientRowId)} size="small" color="error"><DeleteOutlineIcon fontSize="small" /></IconButton> : null}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
          <Typography sx={{ color: "neutral.700", fontSize: 12, fontWeight: 700 }}>
            {ingredients.length} insumo(s) · {rows.length} presentación(es) · {dirtyRows.length} cambio(s) pendiente(s) · Enter avanza de celda y ↑/↓ cambia de fila.
          </Typography>
        </Stack>
      </Box>
      <SupplierFormModal
        open={supplierFormOpen}
        onClose={closeSupplierForm}
        onSave={createSupplierForRow}
      />
      <ConfirmChangesDialog open={confirmDiscard || navigationBlocker.state === "blocked"} discardOnly={confirmDiscard} onCancel={() => { setConfirmDiscard(false); navigationBlocker.reset?.(); }} onConfirm={() => { if (confirmDiscard) discardChanges(); else navigationBlocker.proceed?.(); }} />
    </Stack>
  );
}
